import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { InputComponent } from '../../shared/ui/input/input.component';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { REGEX_EMAIL } from 'sources/apps/front-mgr/src/app/core/constants/constants';
import { Router } from '@angular/router';
import { RoutePaths } from 'sources/apps/front-mgr/src/app/core/constants/route-paths';
import { GeneralErrorComponent } from '../../shared/ui/general-error/general-error.component';
import { CardHeaderTitleSubtitleComponent } from '../../shared/ui/card-header-title-subtitle/card-header-title-subtitle.component';
import { AuthVerificationHandlerComponent } from '../../shared/components/auth-verification-handler/auth-verification-handler.component';
import { Subscription } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';
import { SpinnerService } from '../../../core/services/spinner.service';
import { RecaptchaService } from '../../shared/services/recaptch.service';
import { DataSessionService } from '../../../core/services/data-session.service';
import { DataLayerService } from '../../../core/services/data-layer.service';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    InputComponent,
    ReactiveFormsModule,
    GeneralErrorComponent,
    CardHeaderTitleSubtitleComponent,
    AuthVerificationHandlerComponent,
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss',
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  forgotPassForm!: FormGroup;
  showSuccess: boolean = false;
  subscription: Subscription = new Subscription();
  error!: string;
  screenName = 'forgot_password';

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _authenticationService: AuthenticationService,
    private readonly _spinnerService: SpinnerService,
    private readonly _recaptchaService: RecaptchaService,
    private readonly _router: Router,
    private readonly _dataSessionService: DataSessionService,
    private readonly _dataLayerService: DataLayerService
  ) {}

  ngOnInit() {
    this._dataLayerService.pushEventPageView(this.screenName);

    this.forgotPassForm = this._formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(REGEX_EMAIL)]],
      // recaptcha: ['', Validators.required]
    });
  }

  async submitForm(): Promise<void> {
    this.error = '';
    this._spinnerService.show();
    const email = this.forgotPassForm.value;
    this._dataLayerService.pushEventClickButton(
      this.screenName,
      'accept',
      'forgot-password.btn-accept'
    );
    try {
      const token = await this._recaptchaService.reCAPTCHA('RESET_PASSWORD');

      const sub = this._authenticationService
        .postResetPassword(email)
        .subscribe({
          next: (response) => {
            this.showSuccess = true;
            this._dataLayerService.pushEventSubmit(
              this.screenName,
              'reset_password',
              'Password reset request successful',
              'body button'
            );
          },
          error: (err) => {
            if (err.error.state === 'EXC07') {
              this._dataSessionService.showModalRecaptchaError(() =>
                this.reloadForm()
              );
            } else if (err.status == 400) {
              this.error = 'forgot-password.wrong-user';
            } else {
              this.error = 'generic';
            }

            this._dataLayerService.pushEventSubmitError(
              this.screenName,
              'reset_password',
              err.error.state || err.status,
              'Password reset request failed'
            );
            this._spinnerService.hide();
          },
          complete: () => {
            this._spinnerService.hide();
          },
        });

      this.subscription.add(sub);
    } catch (error) {
      this._spinnerService.hide();
      this._dataLayerService.pushEventSubmitError(
        this.screenName,
        'recaptcha_error',
        'reset_password',
        'Error executing reCAPTCHA from forgot password form'
      );
      console.error('Error executing reCAPTCHA', error);
    }
  }

  goBackFromVerification(e: boolean): void {
    this.showSuccess = false;
  }

  async handleRedirect(): Promise<void> {
    this._dataLayerService.pushEventClickLink(
      this.screenName,
      'back',
      'forgot-password.btn-back'
    );
    await this._router.navigate([`${RoutePaths.login}`]);
  }

  reloadForm(): void {
    this.forgotPassForm.reset();
    this.error = '';
    this._spinnerService.hide();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
